import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Subscription, catchError, filter, forkJoin, interval, of } from 'rxjs';
import { PageShellComponent, TabStripComponent, TabStripItem } from '@clinic-platform/ui-components';
import { DowntimeService } from '../data-access/downtime.service';
import { SpecialistDowntimeService } from '../../specialist-downtime/data-access/specialist-downtime.service';
import { AuthService } from '../../../core/auth/auth.service';
import { UserRole } from '../../../core/auth/user-role.type';
import { SystemAdminContextService } from '../../../core/context/system-admin-context.service';

@Component({
  selector: 'app-downtime-tabs-page',
  standalone: true,
  imports: [PageShellComponent, TabStripComponent, RouterOutlet],
  template: `
    <lib-page-shell>
      <lib-tab-strip
        [items]="tabs"
        [activeId]="activeTab"
        (activeIdChange)="onTabChange($event)"
        ariaLabel="Downtime type tabs"
      />

      <router-outlet />
    </lib-page-shell>
  `,
})
export class DowntimeTabsPageComponent implements OnInit, OnDestroy {
  private readonly deviceDowntimeService = inject(DowntimeService);
  private readonly specialistDowntimeService = inject(SpecialistDowntimeService);
  private readonly authService = inject(AuthService);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly subscriptions = new Subscription();
  private readonly role = this.authService.getCurrentRole();
  private readonly isSystemAdmin = this.role === 'system_admin';

  protected tabs: TabStripItem[] = this.buildTabs();

  protected activeTab: string = this.tabs[0]?.id ?? 'device';

  public ngOnInit(): void {
    this.updateActiveTabByUrl(this.router.url);
    this.refreshPendingIndicators();
    this.subscriptions.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe((event) => {
          this.updateActiveTabByUrl((event as NavigationEnd).urlAfterRedirects);
          this.refreshPendingIndicators();
        }),
    );

    this.subscriptions.add(interval(60000).subscribe(() => this.refreshPendingIndicators()));

    if (this.isSystemAdmin) {
      this.subscriptions.add(this.systemAdminContext.companyId$.subscribe(() => this.refreshPendingIndicators()));
      this.subscriptions.add(this.systemAdminContext.scopeMode$.subscribe(() => this.refreshPendingIndicators()));
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected onTabChange(tabId: string): void {
    if (!this.tabs.some((tab) => tab.id === tabId)) {
      return;
    }

    void this.router.navigate([tabId], { relativeTo: this.route });
  }

  private updateActiveTabByUrl(url: string): void {
    const clean = (url ?? '').split('?')[0];
    if (clean.includes('/downtime/specialist') && this.canAccessSpecialistTab()) {
      this.activeTab = 'specialist';
      return;
    }

    if (clean.includes('/downtime/device') && this.canAccessDeviceTab()) {
      this.activeTab = 'device';
      return;
    }

    this.activeTab = this.tabs[0]?.id ?? 'device';
  }

  private refreshPendingIndicators(): void {
    if (!this.hasRequiredCompanyContext()) {
      this.setPendingDots(false, false);
      return;
    }

    const device$ = this.canAccessDeviceTab()
      ? this.deviceDowntimeService.getPendingSummary().pipe(catchError(() => of({ pendingCount: 0, hasPending: false })))
      : of({ pendingCount: 0, hasPending: false });
    const specialist$ = this.canAccessSpecialistTab()
      ? this.specialistDowntimeService.getPendingSummary().pipe(catchError(() => of({ pendingCount: 0, hasPending: false })))
      : of({ pendingCount: 0, hasPending: false });

    forkJoin({
      device: device$,
      specialist: specialist$,
    }).subscribe((summary) => {
      this.setPendingDots(summary.device.hasPending, summary.specialist.hasPending);
    });
  }

  private setPendingDots(deviceHasPending: boolean, specialistHasPending: boolean): void {
    this.tabs = this.tabs.map((tab) => {
      const hasPending = (tab.id === 'device' && deviceHasPending)
        || (tab.id === 'specialist' && specialistHasPending);

      return {
        ...tab,
        dot: hasPending,
        dotTitle: hasPending ? 'Pending approvals' : undefined,
        dotColor: hasPending ? '#ef4444' : undefined,
        dotRingColor: hasPending ? 'rgba(239, 68, 68, 0.35)' : undefined,
        dotBlinking: hasPending,
        dotBlinkPeriodMs: hasPending ? 1200 : undefined,
      } satisfies TabStripItem;
    });
  }

  private hasRequiredCompanyContext(): boolean {
    if (!this.isSystemAdmin) {
      return true;
    }

    if (this.systemAdminContext.isGlobalScope()) {
      return false;
    }

    return !!this.systemAdminContext.getCompanyId()?.trim();
  }

  private buildTabs(): TabStripItem[] {
    const tabs: TabStripItem[] = [];

    if (this.canAccessDeviceTab()) {
      tabs.push({ id: 'device', label: 'Device' });
    }

    if (this.canAccessSpecialistTab()) {
      tabs.push({ id: 'specialist', label: 'Specialist' });
    }

    return tabs;
  }

  private canAccessDeviceTab(): boolean {
    return hasAnyRole(this.role, ['system_admin', 'company_admin', 'manager', 'technician', 'reception']);
  }

  private canAccessSpecialistTab(): boolean {
    return hasAnyRole(this.role, ['system_admin', 'company_admin', 'manager', 'specialist', 'reception']);
  }
}

@Component({
  selector: 'app-downtime-default-redirect',
  standalone: true,
  template: '',
})
export class DowntimeDefaultRedirectComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  public ngOnInit(): void {
    const role = this.authService.getCurrentRole() as UserRole | null;
    const target = role === 'specialist' ? 'specialist' : 'device';

    void this.router.navigate([target], {
      relativeTo: this.route.parent ?? this.route,
      replaceUrl: true,
    });
  }
}

function hasAnyRole(role: string | null, allowedRoles: UserRole[]): boolean {
  return !!role && allowedRoles.includes(role as UserRole);
}
